/**
 * MDF-208: the "Active Filters" box of the Export modal.
 *
 * The box names the screen filters that the export applies, with the labels
 * of the filter panel. It also names the screen filters that the export
 * ignores, so the file never looks filtered when it is not. This module has
 * no React code, so a node script can test it.
 */
import type { ActiveFilterRow, FilterFieldConfig, QuickFilterConfig } from '../FilterPanel/FilterPanel';

export interface FilterSummaryItem {
  label: string;
  value: string;
}

/** The filter state of one table screen. */
export interface ScreenFilters {
  search?: string;
  quickFilters?: QuickFilterConfig[];
  activeQuickFilters?: string[];
  fields: FilterFieldConfig[];
  rows: ActiveFilterRow[];
}

export interface SplitFilters {
  /** Filters that are set and that the export applies. */
  applied: FilterSummaryItem[];
  /** Labels of the filters that are set and that the export ignores. */
  ignored: string[];
}

/**
 * Splits the set screen filters by the keys the export applies.
 * The key 'search' stands for the search box; a quick filter uses its own key.
 */
export function splitScreenFilters(screen: ScreenFilters, appliedKeys: string[]): SplitFilters {
  const applied: FilterSummaryItem[] = [];
  const ignored: string[] = [];
  const add = (key: string, label: string, value: string) => {
    if (appliedKeys.includes(key)) applied.push({ label, value });
    else ignored.push(label);
  };

  const search = screen.search?.trim();
  if (search) add('search', 'Search', search);
  for (const quick of screen.quickFilters ?? []) {
    if (screen.activeQuickFilters?.includes(quick.key)) add(quick.key, quick.label, 'On');
  }
  for (const row of screen.rows) {
    if (row.values.length === 0) continue;
    const field = screen.fields.find(f => f.key === row.key);
    const optionLabel = (value: string) => field?.options.find(o => o.value === value)?.label ?? value;
    add(row.key, field?.label ?? row.key, row.values.map(optionLabel).join(', '));
  }
  return { applied, ignored };
}

/** What the export covers when it applies no filter. */
export type ExportScope = 'cohort' | 'cohorts' | 'all';

const EVERY_ROW: Record<ExportScope, string> = {
  cohort: 'It includes every row in this cohort.',
  cohorts: 'It includes every row in the selected cohorts.',
  all: 'It includes every row.',
};

/** The plain notice for screen filters the export ignores, or null when there are none. */
export function ignoredFiltersNotice(ignored: string[], appliedCount: number, scope: ExportScope): string | null {
  if (ignored.length === 0) return null;
  const rest = appliedCount > 0 ? 'It applies only the filters above.' : EVERY_ROW[scope];
  return `The export ignores these screen filters: ${ignored.join(', ')}. ${rest}`;
}

/**
 * The modal title. The count is the grid count, so it shows only when the file
 * holds the same rows: no ignored screen filter, and the filters are not dropped.
 */
export function exportTitle(entityLabel: string, recordCount: number | undefined, countMatchesFile: boolean): string {
  if (recordCount === undefined || !countMatchesFile) return `Export ${entityLabel}`;
  return `Export ${entityLabel} (${recordCount.toLocaleString()} ${recordCount === 1 ? 'record' : 'records'})`;
}

/** The Export modal props for one split. */
export function exportSummaryProps(split: SplitFilters): { filterSummary: FilterSummaryItem[]; ignoredFilters: string[] } {
  return { filterSummary: split.applied, ignoredFilters: split.ignored };
}
